import { useState } from 'react';

import type { GetEffectivePolicyParams } from '../api';
import { useEffectivePolicy } from '../hooks/usePreparationPolicies';

const COMMON_MIMES: { label: string; value: string }[] = [
  { label: 'PDF', value: 'application/pdf' },
  { label: 'DOCX', value: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' },
  { label: 'XLSX', value: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' },
  { label: 'PPTX', value: 'application/vnd.openxmlformats-officedocument.presentationml.presentation' },
  { label: 'HTML', value: 'text/html' },
  { label: 'EML', value: 'message/rfc822' },
  { label: 'PNG', value: 'image/png' },
  { label: 'JPEG', value: 'image/jpeg' },
];

/** Color tag per route for visual continuity with the runbook + dashboards. */
function routeBadgeClass(route: string): string {
  switch (route) {
    case 'MarkItDownLocal': return 'bg-emerald-50 text-emerald-700 ring-emerald-200';
    case 'MarkItDownDocIntel': return 'bg-sky-50 text-sky-700 ring-sky-200';
    case 'MarkItDownContentUnderstanding': return 'bg-violet-50 text-violet-700 ring-violet-200';
    case 'DocIntelLegacy': return 'bg-slate-100 text-slate-700 ring-slate-200';
    case 'ContentUnderstandingNative': return 'bg-amber-50 text-amber-700 ring-amber-200';
    case 'LlmVisionFallback': return 'bg-rose-50 text-rose-700 ring-rose-200';
    default: return 'bg-slate-50 text-slate-700 ring-slate-200';
  }
}

export function PreparationEffectivePolicyPage(): JSX.Element {
  const [tenantId, setTenantId] = useState('');
  const [mime, setMime] = useState('application/pdf');
  const [digital, setDigital] = useState(true);
  const [documentTypeId, setDocumentTypeId] = useState('');

  // Build params conditionally (exactOptionalPropertyTypes: don't pass undefined for optional fields).
  const params: Partial<GetEffectivePolicyParams> = { mime, digital };
  const trimmedTenant = tenantId.trim();
  const trimmedDocType = documentTypeId.trim();
  if (trimmedTenant) params.tenantId = trimmedTenant;
  if (trimmedDocType) params.documentTypeId = trimmedDocType;

  const { data, isLoading, isError, error } = useEffectivePolicy(params);

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-2xl font-semibold text-slate-900">Política efectiva de preparación</h1>
        <p className="mt-1 text-sm text-slate-500">
          Visor de la cadena que el router resolvería para un (tenant, tipo de documento, MIME).
          Idéntico a lo que el pipeline aplica en runtime — no edita nada, solo muestra.
        </p>
      </header>

      <section className="card grid gap-4 p-5 md:grid-cols-4">
        <label className="block">
          <span className="mb-1 block text-xs font-medium text-slate-600">Tenant ID</span>
          <input
            type="text"
            value={tenantId}
            onChange={(e) => setTenantId(e.target.value)}
            placeholder="tenant_abc123"
            className="w-full rounded border border-slate-300 px-3 py-2 text-sm focus:border-primary focus:ring-1 focus:ring-primary"
          />
        </label>
        <label className="block">
          <span className="mb-1 block text-xs font-medium text-slate-600">MIME</span>
          <select
            value={mime}
            onChange={(e) => setMime(e.target.value)}
            className="w-full rounded border border-slate-300 px-3 py-2 text-sm focus:border-primary focus:ring-1 focus:ring-primary"
          >
            {COMMON_MIMES.map((m) => (
              <option key={m.value} value={m.value}>{m.label}</option>
            ))}
          </select>
        </label>
        <label className="block">
          <span className="mb-1 block text-xs font-medium text-slate-600">Document type (opcional)</span>
          <input
            type="text"
            value={documentTypeId}
            onChange={(e) => setDocumentTypeId(e.target.value)}
            placeholder="dt_invoice_es"
            className="w-full rounded border border-slate-300 px-3 py-2 text-sm focus:border-primary focus:ring-1 focus:ring-primary"
          />
        </label>
        <label className="flex items-end gap-2 pb-2">
          <input
            type="checkbox"
            checked={digital}
            onChange={(e) => setDigital(e.target.checked)}
            className="h-4 w-4 rounded border-slate-300"
          />
          <span className="text-sm text-slate-700">Digital (tiene capa de texto)</span>
        </label>
      </section>

      {!tenantId.trim() && (
        <div className="card border-slate-200 bg-slate-50 p-4 text-sm text-slate-600">
          Introduce un <code className="font-mono">tenantId</code> para resolver la política.
        </div>
      )}

      {isError && (
        <div className="card border-rose-200 bg-rose-50 p-4 text-sm text-rose-800">
          {error instanceof Error ? error.message : 'Error al resolver la política efectiva.'}
        </div>
      )}

      {isLoading && (
        <div className="card p-5 text-sm text-slate-500">Resolviendo…</div>
      )}

      {data && (
        <section className="card space-y-4 p-5">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-medium text-slate-700">Cadena resolvida</h2>
            <span
              className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ring-1 ${
                data.source === 'tenant'
                  ? 'bg-primary/10 text-primary ring-primary/20'
                  : 'bg-slate-100 text-slate-600 ring-slate-200'
              }`}
            >
              source: {data.source}
            </span>
          </div>

          {data.chain.length === 0 ? (
            <p className="text-sm text-rose-700">
              Cadena vacía — el router devolvería <code>RouteUnavailable</code> para este escenario.
            </p>
          ) : (
            <ol className="flex flex-wrap items-center gap-2">
              {data.chain.map((route, idx) => (
                <li key={`${route}-${idx}`} className="flex items-center gap-2">
                  <span className="font-mono text-xs text-slate-400">{idx + 1}.</span>
                  <span className={`inline-flex items-center rounded px-2 py-1 text-xs font-medium ring-1 ${routeBadgeClass(route)}`}>
                    {route}
                  </span>
                  {idx < data.chain.length - 1 && <span className="text-slate-400">→</span>}
                </li>
              ))}
            </ol>
          )}

          {data.forbiddenStripped.length > 0 && (
            <div className="border-t border-slate-100 pt-3">
              <p className="mb-2 text-xs text-slate-500">Rutas eliminadas por la lista de prohibidas del tenant:</p>
              <ul className="flex flex-wrap gap-2">
                {data.forbiddenStripped.map((r) => (
                  <li key={r} className="inline-flex items-center rounded px-2 py-0.5 text-xs font-medium bg-rose-50 text-rose-700 ring-1 ring-rose-200 line-through">
                    {r}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </section>
      )}
    </div>
  );
}
